// cSpell:ignore Gemeenschappelijk Privatief Provisies
// Adding, or changing, a provision by hand in the Provisies step (Lewis,
// 2026-10-16): a small modal with the provision's description, its amount
// for this settlement and the kind it counts in (decision 20, 2026-10-20:
// "Gemeenschappelijk" or "Privatief"; a kind the lease has no provision for
// cannot be chosen, and the modal says why), checked before anything is
// kept. It replaced an inline row of inputs that saved the draft at every
// keystroke, cut its own placeholder off and sat out of line with the other
// lines. The costs feature opens its forms in the same Modal.

import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Banner,
  Labelled,
  Modal,
  OptionListShared,
  Stack,
  TextField,
} from "@rentiohq/web-shared/dist/components";
import React from "react";
import { LocaleNumberField } from "../../../../components/LocaleNumberField";
import {
  checkExtraProvision,
  defaultExtraProvisionKind,
  IExtraProvisionKindOption,
  TExtraProvisionValues,
} from "../../../../extraProvisionForm.utils";
import { formatMoney } from "../../../../moneyText.utils";
import { MONEY_DECIMALS } from "../../../../numberInput.utils";

const KIND_LABEL_KEYS: Record<"common" | "private", string> = {
  common: "settlement.provisions.extra_form.kind_common",
  private: "settlement.provisions.extra_form.kind_private",
};

export const ExtraProvisionModal: React.FC<{
  tenantName: string;
  // The kinds the lease weighs here; one without a provision is disabled.
  kindOptions: IExtraProvisionKindOption[];
  // The provision being changed; absent when one is added.
  initial?: TExtraProvisionValues;
  onClose: () => void;
  onSubmit: (values: TExtraProvisionValues) => void;
}> = ({ tenantName, kindOptions, initial, onClose, onSubmit }) => {
  const [title, setTitle] = React.useState(initial?.title ?? "");
  const [amount, setAmount] = React.useState<number | undefined>(
    initial?.amount,
  );
  const [kind, setKind] = React.useState<"common" | "private" | undefined>(
    initial
      ? initial.kind ?? "private"
      : defaultExtraProvisionKind(kindOptions),
  );
  // What is wrong shows once the broker tried to keep it, then follows
  // every change.
  const [hasTriedSubmit, setHasTriedSubmit] = React.useState(false);
  const check = checkExtraProvision({ title, amount, kind, kindOptions });
  const disabledReasons = kindOptions
    .map(option => option.disabledReason)
    .filter((reason): reason is string => !!reason);

  const handleSubmit = () => {
    setHasTriedSubmit(true);
    if (check.values) onSubmit(check.values);
  };

  return (
    <Modal
      width="small"
      heading={getLocalizedText(
        initial
          ? "settlement.provisions.extra_form.title_edit"
          : "settlement.provisions.extra_form.title_add",
        { tenant: tenantName },
      )}
      onClose={onClose}
      actions={[
        {
          content: getLocalizedText("costs.action.cancel"),
          onClick: onClose,
          appearance: "outline",
        },
        {
          content: getLocalizedText(
            initial ? "costs.action.save" : "system.add",
          ),
          onClick: handleSubmit,
          appearance: "primary",
        },
      ]}
    >
      {/* Enter in either field keeps the provision, as the button does. */}
      <div
        onKeyDown={event => {
          if (event.key !== "Enter") return;
          event.preventDefault();
          handleSubmit();
        }}
      >
        <Stack vertical spacing="loose">
          <TextField
            name="extra-provision-description"
            label={getLocalizedText("settlement.provisions.extra_title_label")}
            placeholder={getLocalizedText(
              "settlement.provisions.extra_title_placeholder",
            )}
            value={title}
            autoFocus
            error={hasTriedSubmit ? check.errors.title : undefined}
            onChange={(
              event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
            ) => setTitle(event.target.value)}
          />
          <LocaleNumberField
            name="extra-provision-amount"
            label={getLocalizedText(
              "settlement.provisions.extra_form.amount_label",
            )}
            isMoney
            maxDecimals={MONEY_DECIMALS}
            placeholder={formatMoney(0)}
            value={amount}
            error={hasTriedSubmit ? check.errors.amount : undefined}
            onChange={setAmount}
          />
          <Labelled
            label={getLocalizedText(
              "settlement.provisions.extra_form.kind_label",
            )}
            error={hasTriedSubmit ? check.errors.kind : undefined}
          >
            <OptionListShared
              id="extra-provision-kind"
              multiple={false}
              value={kind ? [kind] : []}
              options={kindOptions.map(option => ({
                id: option.kind,
                value: option.kind,
                disabled: !!option.disabledReason,
                label: getLocalizedText(KIND_LABEL_KEYS[option.kind]),
              }))}
              onChange={(selected: string[]) => {
                const next = selected[0];
                if (next === "common" || next === "private") setKind(next);
              }}
            />
          </Labelled>
          {/* A disabled option takes no pointer events, so its reason is
              said under the options rather than in a tooltip. */}
          {disabledReasons.length > 0 && (
            <Banner variation="info" hasDismiss={false}>
              {disabledReasons.map(reason => (
                <div key={reason}>{reason}</div>
              ))}
            </Banner>
          )}
        </Stack>
      </div>
    </Modal>
  );
};
